import React, { useEffect, useRef, useState } from "react";
import { TextField, Tooltip } from "@material-ui/core";
import { useTranslation } from "../../hooks/useTranslation";
import { formatHHMMSS, parseHHMMSS } from "../../utils/transcriptionFormat";

/**
 * Mini-editor de tempo no formato hh:mm:ss.
 *
 * Em modo somente leitura, é uma `<span>` clicável que apenas exibe o tempo.
 * Em modo editável, alterna para um `<input>` quando clicado/foco; ao
 * confirmar, valida que o novo tempo respeita os limites:
 *   - tempo > tempo da frase anterior (`minSeconds`, exclusivo) — quando dado
 *   - tempo < tempo da frase posterior (`maxSeconds`, exclusivo) — quando dado
 *
 * Props:
 *   - `seconds`     valor atual (number, em segundos)
 *   - `editable`    boolean
 *   - `minSeconds`  number|null  (default: null = sem limite inferior)
 *   - `maxSeconds`  number|null  (default: null = sem limite superior)
 *   - `onChange`    (newSeconds:number) => void
 *   - `onSeek`      callback opcional para que clicar (sem editar) leve o
 *                   player para esse tempo.
 */
function TimestampInput({
  seconds,
  editable = false,
  minSeconds = null,
  maxSeconds = null,
  onChange,
  onSeek,
  className,
}) {
  const { t } = useTranslation("common");
  const [editing, setEditing] = useState(false);
  const [text, setText] = useState(formatHHMMSS(seconds));
  const [error, setError] = useState(null);
  const inputRef = useRef(null);

  useEffect(() => {
    if (!editing) setText(formatHHMMSS(seconds));
  }, [seconds, editing]);

  useEffect(() => {
    if (editing && inputRef.current) {
      inputRef.current.select();
    }
  }, [editing]);

  const tooltip = editable
    ? t("transcription.list.timeEditTooltip")
    : onSeek
      ? t("transcription.list.timeSeekTooltip")
      : "";

  const validate = (newText) => {
    const parsed = parseHHMMSS(newText);
    if (parsed == null) {
      return { ok: false, error: t("transcription.list.timeInvalid") };
    }
    /* Comparações em float: o limite inferior é estritamente o `end` da
       frase ANTERIOR, o limite superior é estritamente o `start` da
       PRÓXIMA. Como `parsed` é inteiro (parseHHMMSS) e o limite pode ser
       fracionário, mostramos NA MENSAGEM o menor/maior inteiro de fato
       digitável, e não o `floor` do limite (que confundia: usuário via
       "00:00:14" para um limite real de 14.7s e digitava 14, falhando). */
    if (minSeconds != null && Number.isFinite(minSeconds)) {
      if (parsed <= minSeconds) {
        const minAllowed = Math.floor(minSeconds) + 1;
        return {
          ok: false,
          error: t("transcription.list.timeTooEarly", {
            min: formatHHMMSS(minAllowed),
          }),
        };
      }
    }
    if (maxSeconds != null && Number.isFinite(maxSeconds)) {
      if (parsed >= maxSeconds) {
        const maxAllowed = Math.max(0, Math.ceil(maxSeconds) - 1);
        return {
          ok: false,
          error: t("transcription.list.timeTooLate", {
            max: formatHHMMSS(maxAllowed),
          }),
        };
      }
    }
    return { ok: true, value: parsed };
  };

  const commit = () => {
    const result = validate(text);
    if (!result.ok) {
      setError(result.error);
      return;
    }
    setError(null);
    setEditing(false);
    if (result.value !== Math.floor(seconds || 0)) {
      onChange?.(result.value);
    }
  };

  const cancel = () => {
    setText(formatHHMMSS(seconds));
    setError(null);
    setEditing(false);
  };

  if (editing && editable) {
    return (
      <TextField
        inputRef={inputRef}
        size="small"
        variant="outlined"
        value={text}
        error={Boolean(error)}
        helperText={error || ""}
        onChange={(e) => setText(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            e.preventDefault();
            commit();
          } else if (e.key === "Escape") {
            e.preventDefault();
            cancel();
          }
        }}
        className={["transcription-time-input", className]
          .filter(Boolean)
          .join(" ")}
        inputProps={{
          "aria-label": t("transcription.list.timeAriaLabel"),
          style: {
            fontFamily:
              'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
            fontSize: "0.8rem",
            padding: "4px 6px",
            width: 76,
          },
        }}
      />
    );
  }

  const handleClick = (e) => {
    e.stopPropagation();
    if (editable) {
      setEditing(true);
    } else if (onSeek) {
      onSeek(seconds || 0);
    }
  };

  return (
    <Tooltip title={tooltip} placement="top" arrow disableHoverListener={!tooltip}>
      <span
        className={[
          "transcription-time-display",
          editable || onSeek ? "is-clickable" : "",
          className,
        ]
          .filter(Boolean)
          .join(" ")}
        onClick={handleClick}
        role={editable || onSeek ? "button" : undefined}
        tabIndex={editable || onSeek ? 0 : undefined}
        onKeyDown={(e) => {
          if ((e.key === "Enter" || e.key === " ") && (editable || onSeek)) {
            e.preventDefault();
            handleClick(e);
          }
        }}
      >
        {formatHHMMSS(seconds)}
      </span>
    </Tooltip>
  );
}

export default TimestampInput;
